import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './AdminFirmDetail.module.css';
import { firmApi } from '../../services/firmApi';
import { FirmDetailed, Country } from '../../types/firm';

interface FirmDetailParams {
  firmId: string;
}

const AdminFirmDetail: React.FC = () => {
  // Get the firm ID from URL params
  const { firmId } = useParams<{ firmId: string }>();
  
  // Separate formData state that won't be overwritten by firm state changes
  const [formData, setFormData] = useState<Partial<FirmDetailed>>({
    name: '',
    abbrev: '',
    url: '',
    domain: '',
    Country_ID: null,
    logo: '',
    primary_color: '',
    secondary_color: '',
    status: 1
  });
  const [firm, setFirm] = useState<FirmDetailed | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isDeleted, setIsDeleted] = useState<boolean>(false);
  const navigate = useNavigate();
  const isMountedRef = useRef<boolean>(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const countriesLoadedRef = useRef<boolean>(false);

  // Avoid navigating too quickly which can cause ResizeObserver issues
  const navigateWithDelay = (path: string) => {
    // Short delay to allow cleanup
    setTimeout(() => {
      if (isMountedRef.current) {
        navigate(path);
      }
    }, 50);
  };

  useEffect(() => {
    if (firmId) {
      console.log('Initializing component for firm ID:', firmId);
      
      // Load countries first, and wait for it to complete
      loadData();
    }
    
    // Cleanup function to handle unmounting gracefully
    return () => {
      isMountedRef.current = false;
    };
  }, [firmId]);

  const loadData = async () => {
    // Don't load data if the firm has been deleted
    if (isDeleted) {
      return;
    }
    
    try {
      // Load countries first and wait for them to load
      const countriesData = await fetchCountries();
      
      // Then load firm data after countries are loaded
      if (countriesData.length > 0) {
        await fetchFirm();
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  useEffect(() => {
    // Set image preview when firm data is loaded
    if (firm?.logo) {
      setImagePreview(`data:image/png;base64,${firm.logo}`);
    }
    // WICHTIG: Keine Synchronisierung von formData mit firm-Änderungen
    // Dies verhindert das Überschreiben von Benutzereingaben
  }, [firm?.logo]); // Nur abhängig von logo-Änderungen

  const fetchFirm = async () => {
    // Don't fetch if the firm has been deleted
    if (isDeleted) {
      return;
    }
    
    try {
      setLoading(true);
      const firmData = await firmApi.getFirmById(firmId!);
      
      // Log the original data to debug
      console.log('Original firm data from API:', firmData);
      console.log('Original Country_ID type:', typeof firmData.Country_ID, 'Value:', firmData.Country_ID);
      
      // Create a copy for modifications
      const normalizedFirmData = { ...firmData };
      
      // Ensure Country_ID is properly handled (convert from string if needed)
      if (normalizedFirmData.Country_ID !== null && normalizedFirmData.Country_ID !== undefined) {
        // Always store as string internally
        normalizedFirmData.Country_ID = String(normalizedFirmData.Country_ID);
        console.log('Normalized Country_ID to string:', normalizedFirmData.Country_ID);
      } else {
        // Ensure it's explicitly null, not undefined
        normalizedFirmData.Country_ID = null;
      }
      
      // Ensure all optional fields are at least empty strings instead of undefined
      // This helps with controlled components
      normalizedFirmData.url = normalizedFirmData.url || '';
      normalizedFirmData.domain = normalizedFirmData.domain || '';
      normalizedFirmData.primary_color = normalizedFirmData.primary_color || '';
      normalizedFirmData.secondary_color = normalizedFirmData.secondary_color || '';
      normalizedFirmData.logo = normalizedFirmData.logo || '';
      
      // Store initial data
      console.log('Setting initial firm and formData state with:', normalizedFirmData);
      
      // Update both states with normalized data
      setFirm(normalizedFirmData);
      setFormData(normalizedFirmData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching firm:', error);
      setError('Failed to load firm data. Please try again later.');
      setLoading(false);
      toast.error('Failed to load firm data', {
        position: "top-right",
        autoClose: 5000
      });
    }
  };

  const fetchCountries = async () => {
    try {
      console.log('Fetching countries for firm management...');
      const countriesData = await firmApi.getCountriesLookupForFirms();
      console.log('Original countries from API:', countriesData);
      
  // Normalize country data to ensure Country_ID is always a string
      const normalizedCountries = countriesData.map((country: Country) => ({
        ...country,
        Country_ID: String(country.Country_ID)
      }));
      
      console.log('Normalized countries:', normalizedCountries);
      setCountries(normalizedCountries);
      countriesLoadedRef.current = true;
      
      return normalizedCountries; // Return the normalized countries
    } catch (error) {
      console.error('Error fetching countries:', error);
      // Display backend error detail if available
      const errorMessage = error instanceof Error ? error.message : 'Failed to load countries data';
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000
      });
      return []; // Return empty array on error
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    const fieldName = id.replace('input', '');
    
    console.log(`Input changed in detail view: ${id} -> fieldName: ${fieldName}, value: "${value}", event target value: "${e.target.value}"`);
    
    // Using the direct property assignment approach from AdminUserDetail.tsx
    if (fieldName === 'Country_ID') {
      if (value === '') {
        setFormData(prev => {
          console.log('Setting Country_ID to null, previous state:', prev);
          const updated = { ...prev, Country_ID: null };
          console.log('New state will be:', updated);
          return updated;
        });
      } else {
        setFormData(prev => {
          console.log('Setting Country_ID to string:', value, 'previous state:', prev);
          const updated = { ...prev, Country_ID: value };
          console.log('New state will be:', updated);
          return updated;
        });
      }
    } else {
      // For regular fields, use a simple direct property assignment
      setFormData(prev => {
        console.log(`Updating ${fieldName} to: "${value}", previous state:`, prev);
        const updated = { ...prev, [fieldName]: value };
        console.log('New state will be:', updated);
        return updated;
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size exceeds 5MB limit', {
          position: "top-right",
          autoClose: 3000
        });
        return;
      }

      // Check file type
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        toast.error('Only JPG and PNG files are allowed', {
          position: "top-right",
          autoClose: 3000
        });
        return;
      }

      // Read file as base64
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64String = event.target?.result as string;
        // Extract only the base64 data part
        const base64Data = base64String.split(',')[1];
        setImagePreview(base64String);
        
        // Update formData using functional update to prevent stale state
        setFormData(prevFormData => ({
          ...prevFormData,
          logo: base64Data
        }));
        
        console.log('Updated logo in formData state');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const validateForm = () => {
    // Check required fields - all fields except logo are required
    if (!formData.name || formData.name.trim() === '') {
      toast.error('Firm name is required', {
        position: "top-right",
        autoClose: 3000
      });
      return false;
    }

    if (!formData.abbrev || formData.abbrev.trim() === '') {
      toast.error('Firm abbreviation is required', {
        position: "top-right",
        autoClose: 3000
      });
      return false;
    }

    if (!formData.url || formData.url.trim() === '') {
      toast.error('Firm URL is required', {
        position: "top-right",
        autoClose: 3000
      });
      return false;
    }

    if (!formData.domain || formData.domain.trim() === '') {
      toast.error('Mail domain is required', {
        position: "top-right",
        autoClose: 3000
      });
      return false;
    }

    if (formData.Country_ID === null) {
      toast.error('Country is required', {
        position: "top-right",
        autoClose: 3000
      });
      return false;
    }

    return true;
  };

  // Neue Funktion, die mit übergebenen Daten arbeitet statt mit dem globalen formData
  const validateFormWithData = (data: Partial<FirmDetailed>) => {
    // Check required fields - all fields except logo are required
    if (!data.name || data.name.trim() === '') {
      toast.error('Firm name is required', {
        position: "top-right",
        autoClose: 3000
      });
      return false;
    }

    if (!data.abbrev || data.abbrev.trim() === '') {
      toast.error('Firm abbreviation is required', {
        position: "top-right",
        autoClose: 3000
      });
      return false;
    }

    if (!data.url || data.url.trim() === '') {
      toast.error('Firm URL is required', {
        position: "top-right",
        autoClose: 3000
      });
      return false;
    }

    if (!data.domain || data.domain.trim() === '') {
      toast.error('Mail domain is required', {
        position: "top-right",
        autoClose: 3000
      });
      return false;
    }

    if (data.Country_ID === null) {
      toast.error('Country is required', {
        position: "top-right",
        autoClose: 3000
      });
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    try {
      // Lese die aktuellen DOM-Werte direkt aus den Eingabefeldern für eine zuverlässige Datenerfassung
      const nameInput = document.getElementById('inputName') as HTMLInputElement;
      const abbrevInput = document.getElementById('inputAbbrev') as HTMLInputElement;
      const urlInput = document.getElementById('inputUrl') as HTMLInputElement;
      const domainInput = document.getElementById('inputDomain') as HTMLInputElement;
      const countryInput = document.getElementById('inputCountry_ID') as HTMLSelectElement;
      
      // Aktualisiere formData mit den aktuellen DOM-Werten, bevor es validiert und gesendet wird
      const updatedFormData = {
        ...formData,
        name: nameInput?.value || formData.name,
        abbrev: abbrevInput?.value || formData.abbrev,
        url: urlInput?.value || formData.url,
        domain: domainInput?.value || formData.domain,
  Country_ID: countryInput?.value ? countryInput.value : (countryInput?.value === "" ? null : formData.Country_ID)
      };
      
      // Debug-Ausgaben
      console.log('DOM-Werte direkt aus den Feldern:');
      console.log('Name (DOM):', nameInput?.value, 'Name (State):', formData.name);
      console.log('Abbrev (DOM):', abbrevInput?.value, 'Abbrev (State):', formData.abbrev);
      console.log('URL (DOM):', urlInput?.value, 'URL (State):', formData.url);
      console.log('Domain (DOM):', domainInput?.value, 'Domain (State):', formData.domain);
      console.log('Country (DOM):', countryInput?.value, 'Country (State):', formData.Country_ID);
      
      // Arbeite mit den aktualisierten Daten
      setFormData(updatedFormData);
      
      console.log('Save button clicked, current form data:', updatedFormData);
      
      // Validiere die Daten mit den aktuellen Werten
      if (!validateFormWithData(updatedFormData)) {
        return;
      }

      // Erstelle eine Kopie der aktualisierten Daten für Änderungen
      const dataToSend = { ...updatedFormData };
      
      // Handle Country_ID field correctly
      console.log('Country_ID before save preparation:', dataToSend.Country_ID, 'Type:', typeof dataToSend.Country_ID);

      if (dataToSend.Country_ID === undefined || dataToSend.Country_ID === null || dataToSend.Country_ID === '') {
        dataToSend.Country_ID = null;
        console.log('Setting Country_ID to null');
      } else {
        dataToSend.Country_ID = dataToSend.Country_ID;
        console.log('Using Country_ID value:', dataToSend.Country_ID);
      }
      
      // Delete properties that should not be sent to the API
      delete dataToSend.Firm_ID;
      delete dataToSend.created_at;
      delete dataToSend.updated_at;
      
      // Remove other properties that might be in the response but not in the update model
      if ('country' in dataToSend) {
        delete (dataToSend as any).country;
      }
      
      if ('user_count' in dataToSend) {
        delete (dataToSend as any).user_count;
      }
      
      if ('users' in dataToSend) {
        delete (dataToSend as any).users;
      }
      
      // Ensure all required fields are defined for FirmUpdate
      const firmUpdateData = {
        name: dataToSend.name || '',
        abbrev: dataToSend.abbrev || '',
        Country_ID: dataToSend.Country_ID || null,
        logo: dataToSend.logo || null,
        url: dataToSend.url || null,
        primary_color: dataToSend.primary_color || null,
        secondary_color: dataToSend.secondary_color || null,
        domain: dataToSend.domain || null,
        status: dataToSend.status || 1
      };
      
      console.log('Sending data to API:', firmUpdateData);
      console.log('Country_ID after preparation:', firmUpdateData.Country_ID, 'Type:', typeof firmUpdateData.Country_ID);
      
      // Update the firm
      const updatedFirm = await firmApi.updateFirm(firmId!, firmUpdateData);
      
      console.log('Response from API after update:', updatedFirm);
      console.log('Response Country_ID:', updatedFirm.Country_ID, 'Type:', typeof updatedFirm.Country_ID);
      
      // Create a copy of the response for normalization
      const normalizedFirm = { ...updatedFirm };
      
      // Normalize the returned data
      if (normalizedFirm.Country_ID !== null && normalizedFirm.Country_ID !== undefined) {
        normalizedFirm.Country_ID = String(normalizedFirm.Country_ID);
        console.log('Normalized Country_ID to string:', normalizedFirm.Country_ID);
      } else {
        normalizedFirm.Country_ID = null;
      }
      
      // Ensure optional fields are at least empty strings
      normalizedFirm.url = normalizedFirm.url || '';
      normalizedFirm.domain = normalizedFirm.domain || '';
      normalizedFirm.primary_color = normalizedFirm.primary_color || '';
      normalizedFirm.secondary_color = normalizedFirm.secondary_color || '';
      
      toast.success('Firm updated successfully', {
        position: "top-right",
        autoClose: 3000
      });
      
      // Update firm state with normalized data
      setFirm(normalizedFirm);
      
      // Don't update formData as it would overwrite user input
      // The user should continue working with their current input values
      
    } catch (error) {
      console.error('Error updating firm:', error);
      toast.error('Failed to update firm', {
        position: "top-right",
        autoClose: 3000
      });
    }
  };

  const handleDelete = async () => {
    if (!firmId) {
      return;
    }
    
    // Instead of window.confirm, use toast.warn with a custom UI for confirmation
    toast.warn(
      <div style={{ minWidth: '300px' }}>
        <p className="mb-3 fw-bold">Are you sure you want to delete this firm?</p>
        <div className="d-flex justify-content-center gap-3">
          <button 
            className="btn btn-secondary px-4" 
            onClick={() => toast.dismiss()}
          >
            Cancel
          </button>
          <button 
            className="btn btn-danger px-4" 
            onClick={async () => {
              toast.dismiss();
              try {
                await firmApi.deleteFirm(firmId);
                
                // Mark the firm as deleted to prevent further API calls
                setIsDeleted(true);
                
                toast.success('Firm deleted successfully', {
                  position: "top-right",
                  autoClose: 3000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true,
                  onClose: () => {
                    // Navigate to main firms page after toast expires
                    navigate('/admin/firms');
                  }
                });
                
                // Immediate navigation after a short delay to allow toast to show
                setTimeout(() => {
                  if (isMountedRef.current) {
                    navigate('/admin/firms');
                  }
                }, 3200); // Slightly longer than toast autoClose
                
              } catch (error) {
                console.error('Error deleting firm:', error);
                toast.error('Failed to delete firm. Please try again.', {
                  position: "top-right",
                  autoClose: 3000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true
                });
              }
            }}
          >
            Delete
          </button>
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        closeButton: false,
        className: styles.deleteConfirmationToast,
        style: { 
          maxWidth: '400px', 
          width: '100%',
          padding: '10px'
        }
      }
    );
  };

  return (
    <div className={styles.root}>
      <div className="container mt-4">
        <ToastContainer />

        {/* Breadcrumb */}
        <nav aria-label="breadcrumb">
          <ol className={`breadcrumb ${styles.breadcrumb}`}>
            <li className={`breadcrumb-item ${styles.breadcrumbItem}`}>
              <Link to="/admin"><i className="bi bi-house-door me-1"></i>Admin</Link>
            </li>
            <li className={`breadcrumb-item ${styles.breadcrumbItem}`}>
              <Link to="/admin/firms">Firm Management</Link>
            </li>
            <li className={`breadcrumb-item active ${styles.breadcrumbItem}`} aria-current="page">
              {firm?.name || 'Firm Details'}
            </li>
          </ol>
        </nav>

        <div className="row">
          {/* Firm Logo Card */}
          <div className="col-xl-4">
            <div className={`card mb-4 mb-xl-0 ${styles.userFormCard}`}>
              <div className={`card-header ${styles.cardHeader}`}>Firm Picture</div>
              <div className={`card-body text-center ${styles.cardBody}`}>
                <div className={styles.profilePictureContainer}>
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Firm Logo"
                      className={styles.profileImage}
                    />
                  ) : (
                    <div
                      className={styles.profileImage}
                      style={{ backgroundColor: '#e9ecef' }}
                    ></div>
                  )}
                  <div className={styles.profileImageHelpText}>
                    JPG or PNG no larger than 5 MB
                  </div>
                  <input
                    type="file"
                    id="fileInput"
                    ref={fileInputRef}
                    accept="image/png, image/jpeg"
                    className="d-none"
                    onChange={handleFileChange}
                  />
                  <button 
                    id="uploadButton" 
                    className="btn btn-primary" 
                    type="button"
                    onClick={handleUploadClick}
                  >
                    Upload new image
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Firm Details Card */}
          <div className="col-xl-8">
            <div className={`card mb-4 ${styles.userFormCard}`}>
              <div className={`card-header ${styles.cardHeader}`}>Firm Details</div>
              <div className={`card-body ${styles.cardBody}`}>
                <form>
                  {/* Form Row - Name */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel} htmlFor="inputName">
                      Name
                    </label>
                    <input
                      className={`${styles.formControl} ${
                        !formData.name ? styles.isInvalid : ''
                      }`}
                      id="inputName"
                      type="text"
                      placeholder="Enter firm name"
                      defaultValue={formData.name || ''}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Form Row - Abbrev */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel} htmlFor="inputAbbrev">
                      Abbrev
                    </label>
                    <input
                      className={`${styles.formControl} ${
                        !formData.abbrev ? styles.isInvalid : ''
                      }`}
                      id="inputAbbrev"
                      type="text"
                      placeholder="Enter firm abbreviation"
                      defaultValue={formData.abbrev || ''}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Form Row - URL */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel} htmlFor="inputUrl">
                      URL
                    </label>
                    <input
                      className={`${styles.formControl} ${
                        !formData.url ? styles.isInvalid : ''
                      }`}
                      id="inputUrl"
                      type="url"
                      placeholder="Enter firm URL"
                      defaultValue={formData.url || ''}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Form Row - Country */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel} htmlFor="inputCountry_ID">
                      Country
                    </label>
                    <select
                      className={`${styles.formControl} ${
                        formData.Country_ID === null ? styles.isInvalid : ''
                      }`}
                      id="inputCountry_ID"
                      value={formData.Country_ID !== undefined && formData.Country_ID !== null ? String(formData.Country_ID) : ''}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option 
                          key={country.Country_ID} 
                          value={String(country.Country_ID)}
                        >
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Form Row - Domain */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel} htmlFor="inputDomain">
                      Mail Domain link @*****.*** without @
                    </label>
                    <input
                      className={`${styles.formControl} ${
                        !formData.domain ? styles.isInvalid : ''
                      }`}
                      id="inputDomain"
                      type="text"
                      placeholder="Enter mail domain (without @)"
                      defaultValue={formData.domain || ''}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Save and Delete buttons */}
                  <div className={styles.formButtons}>
                    <button 
                      className={`btn btn-primary me-md-2 ${styles.btnPrimary}`} 
                      type="button" 
                      onClick={handleSave}
                    >
                      Save changes
                    </button>
                    <button 
                      className={`btn btn-danger ${styles.btnDanger}`} 
                      type="button"
                      onClick={handleDelete}
                    >
                      Delete Firm
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  );
};

export default AdminFirmDetail;
